import {
    useEffect,
    useRef,
    useContext,
    useState,
} from "react";
import mapboxgl from "mapbox-gl";
import MapContext from "../../context/MapContext";
import { Box, Text } from "@chakra-ui/react";
import { TbFocus2 } from "react-icons/tb";
import { createPortal } from "react-dom";

function MapRotationCenterIndicator() {
    const { mapRef } = useContext(MapContext);
    const markerRef = useRef(null);
    const markerPortalRef = useRef(null);
    const internalState = useRef({
        isSetup: false,
        rotating: false,
        lastMapRef: null,
    });
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let pollId = null;

        const handleRotateStart = () => {
            if (internalState.current.rotating) {
                return;
            }
            internalState.current.rotating = true;

            // Move marker to center of map and add.
            const center = mapRef.current.getCenter();
            markerRef.current
                .setLngLat([center.lng, center.lat])
                .addTo(mapRef.current);
        };

        const handleRotate = () => {
            if (!internalState.current.rotating) {
                return;
            }

            if (markerRef.current) {
                const center = mapRef.current.getCenter();
                markerRef.current.setLngLat([center.lng, center.lat]);
            }
        };

        const handleRotateEnd = () => {
            if (!internalState.current.rotating) {
                return;
            }
            internalState.current.rotating = false;
            if (markerRef.current) {
                markerRef.current.remove();
            }
        };

        const cleanup = () => {
            if (internalState.current.isSetup && mapRef.current) {
                mapRef.current.off("rotatestart", handleRotateStart);
                mapRef.current.off("rotate", handleRotate);
                mapRef.current.off("rotateend", handleRotateEnd);

                if (markerRef.current) {
                    markerRef.current.remove();
                }

                if (markerPortalRef.current) {
                    markerPortalRef.current = null;
                }
                internalState.current.isSetup = false;
                setReady(false);
            }
        };

        const setup = () => {
            // Create marker and portal if they do not already exist.
            if (!markerPortalRef.current || !markerRef.current) {
                markerPortalRef.current = document.createElement("div");
                markerPortalRef.current.className = "trackMarkerOuter";
                markerRef.current = new mapboxgl.Marker({
                    element: markerPortalRef.current,
                    offset: [0, 0],
                });
            }

            mapRef.current.on("rotatestart", handleRotateStart);
            mapRef.current.on("rotate", handleRotate);
            mapRef.current.on("rotateend", handleRotateEnd);

            internalState.current.isSetup = true;
            setReady(true);
        };

        const trySetup = () => {
            if (cancelled || !mapRef?.current) {
                return false;
            }
            if (mapRef.current != internalState.current.lastMapRef) {
                cleanup();
                setup();
                internalState.current.lastMapRef = mapRef.current;
            }
            return true;
        };

        // Demo map is created in a parent useEffect, so mapRef.current may
        // not exist on the first child effect pass — poll until it does.
        if (!trySetup()) {
            pollId = setInterval(() => {
                if (trySetup() && pollId) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }

        return () => {
            cancelled = true;
            if (pollId) {
                clearInterval(pollId);
            }
            cleanup();
            internalState.current.lastMapRef = null;
        };
    }, [mapRef]);

    return (
        <>
            {ready &&
                markerPortalRef.current &&
                createPortal(
                    <Box
                        w="30px"
                        h="30px"
                        ml="-15px"
                        mt="-15px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bg="rgba(255, 255, 255, 0.4)"
                        borderRadius="50%"
                        border="2px solid rgba(0, 0, 0, 0.7)"
                        boxShadow="0px 0px 0px 2px rgba(255,255,255,0.7)"
                        opacity="0.4"
                        position="relative"
                        userSelect="none"
                        pointerEvents="none"
                    >
                        <Text
                            color="rgba(0, 0, 0, 0.7)"
                            fontSize="32px"
                            mb="0px"
                        >
                            <TbFocus2 />
                        </Text>
                    </Box>,
                    markerPortalRef.current,
                )}
        </>
    );
}

export default MapRotationCenterIndicator;
